import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import * as Icons from 'lucide-react';
import { apiUrl } from '../../../utils/api';
import { adminApiFetch } from '../../../utils/adminApi';

const AVAILABLE_ICONS = [
  'MessageCircle', 'Server', 'ShoppingCart', 'TrendingUp', 'Building', 
  'Globe', 'Mail', 'Phone', 'Settings', 'HelpCircle', 'Search', 
  'Package', 'Zap', 'Target', 'Award', 'BarChart', 'Briefcase',
  'Clock', 'Code', 'Cpu', 'Database', 'FileText', 'Heart',
  'Home', 'Image', 'Link', 'Lock', 'MapPin', 'Monitor',
  'PieChart', 'Rocket', 'Shield', 'Star', 'Tag', 'Users'
];

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Mavi', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'green', label: 'Yeşil', class: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'purple', label: 'Mor', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'orange', label: 'Turuncu', class: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'red', label: 'Kırmızı', class: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'pink', label: 'Pembe', class: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'indigo', label: 'İndigo', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'teal', label: 'Turkuaz', class: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'yellow', label: 'Sarı', class: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'gray', label: 'Gri', class: 'bg-gray-100 text-gray-700 border-gray-200' }
];

export default function AdminGogiQuickReplies() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingReply, setEditingReply] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    question: '',
    icon: 'MessageCircle',
    color: 'blue',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchReplies();
  }, []);

  const fetchReplies = async () => {
    try {
      setLoading(true);
      const res = await adminApiFetch(apiUrl('/backend/admin_api.php?action=getGogiaiQuickReplies'));
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('JSON parse error:', e);
        console.error('Response text:', text);
        throw new Error('Invalid JSON response');
      }
      
      if (data.status === 'success') {
        setReplies(data.replies || []);
      } else {
        setError(data.message || 'Hazır cevaplar yüklenemedi');
      }
    } catch (err) {
      setError('Hazır cevaplar yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const action = editingReply ? 'updateGogiaiQuickReply' : 'createGogiaiQuickReply';
      const payload = editingReply 
        ? { ...formData, id: editingReply.id }
        : formData;

      const res = await adminApiFetch(apiUrl(`/backend/admin_api.php?action=${action}`), {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.status === 'success') {
        setSuccess(data.message);
        setFormData({
          title: '',
          description: '',
          question: '',
          icon: 'MessageCircle',
          color: 'blue',
          sort_order: 0,
          is_active: true
        });
        setEditingReply(null);
        fetchReplies();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'İşlem başarısız');
      }
    } catch (err) {
      setError('İşlem sırasında hata oluştu: ' + err.message);
    }
  };

  const handleEdit = (reply) => {
    setEditingReply(reply);
    setFormData({
      title: reply.title || '',
      description: reply.description || '',
      question: reply.question,
      icon: reply.icon,
      color: reply.color,
      sort_order: reply.sort_order,
      is_active: reply.is_active === 1
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hazır cevabı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const res = await adminApiFetch(apiUrl(`/backend/admin_api.php?action=deleteGogiaiQuickReply&id=${id}`), {
        method: 'DELETE'
      });

      const data = await res.json();

      if (data.status === 'success') {
        setSuccess(data.message);
        fetchReplies();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Silme işlemi başarısız');
      }
    } catch (err) {
      setError('Silme işlemi sırasında hata oluştu: ' + err.message);
    }
  };

  const handleCancel = () => {
    setEditingReply(null);
    setFormData({
      title: '',
      description: '',
      question: '',
      icon: 'MessageCircle',
      color: 'blue',
      sort_order: 0,
      is_active: true
    });
  };

  const getIconComponent = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <Icons.MessageCircle className="w-5 h-5" />;
  };

  const getColorClass = (color) => {
    const colorOption = COLOR_OPTIONS.find(c => c.value === color);
    return colorOption ? colorOption.class : COLOR_OPTIONS[0].class;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6 w-full overflow-x-hidden">
      <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">GogiAI Hazır Cevaplar</h1>
        <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 break-words">
          Widget'ta gösterilecek hazır soruları yönetin. Kullanıcılar bu sorulara tıklayarak hızlıca sohbet başlatabilir.
        </p>
      </div>

      {error && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs sm:text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs sm:text-sm">
          {success}
        </div>
      )}

      {/* Form */}
      <Card className="mb-4 sm:mb-6 overflow-hidden w-full max-w-[85vw]">
        <CardHeader className="p-4 sm:p-6 overflow-x-hidden">
          <CardTitle className="text-base sm:text-lg break-words max-w-full">
            {editingReply ? 'Hazır Cevabı Düzenle' : 'Yeni Hazır Cevap Ekle'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 overflow-x-hidden w-full">
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 w-full max-w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
              <div className="min-w-0 w-full">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                  Başlık (Kısa) *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Örn: Hosting Paketi"
                  className="text-sm sm:text-base w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Widget'ta gösterilecek kısa başlık</p>
              </div>
              
              <div className="min-w-0 w-full">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                  Açıklama (Alt Başlık)
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Örn: Web siteniz için hosting çözümü"
                  className="text-sm sm:text-base w-full"
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Widget'ta gösterilecek açıklama</p>
              </div>
            </div>

            <div className="min-w-0 w-full">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                Soru Metni (GogiAI'ye Gönderilecek) *
              </label>
              <Input
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Örn: Hosting paketleriniz nelerdir? En uygun paketi önerir misiniz?"
                className="text-sm sm:text-base w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1 break-words">Kullanıcı bu kartı tıkladığında GogiAI'ye gönderilecek tam soru</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
              <div className="min-w-0 w-full">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                  İkon
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {AVAILABLE_ICONS.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
                <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <span>Önizleme:</span>
                  {getIconComponent(formData.icon)}
                </div>
              </div>

              <div className="min-w-0 w-full">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                  Renk
                </label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {COLOR_OPTIONS.map(color => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
                <div className="mt-2">
                  <span className={`inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-lg border text-xs sm:text-sm ${getColorClass(formData.color)}`}>
                    {getIconComponent(formData.icon)}
                    <span>Önizleme</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
              <div className="min-w-0 w-full">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 break-words">
                  Sıra
                </label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="text-sm sm:text-base w-full"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Aktif</span>
                </label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button type="submit" className="w-full sm:w-auto bg-blue-600 text-white hover:bg-blue-700 text-sm sm:text-base">
                {editingReply ? 'Güncelle' : 'Ekle'}
              </Button>
              {editingReply && (
                <Button type="button" variant="outline" onClick={handleCancel} className="w-full sm:w-auto text-sm sm:text-base">
                  İptal
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="overflow-hidden w-full max-w-[85vw]">
        <CardHeader className="p-4 sm:p-6 overflow-x-hidden">
          <CardTitle className="text-base sm:text-lg break-words max-w-full">Mevcut Hazır Cevaplar ({replies.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 overflow-x-hidden w-full">
          {replies.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-500 text-center py-6 sm:py-8">Henüz hazır cevap eklenmemiş.</p>
          ) : (
            <div className="space-y-3 w-full">
              {replies.map((reply) => (
                <div
                  key={reply.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 overflow-hidden w-full"
                >
                  <div className="flex items-start sm:items-center gap-3 flex-1 w-full min-w-0 overflow-hidden">
                    <span className={`inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border flex-shrink-0 ${getColorClass(reply.color)}`}>
                      {getIconComponent(reply.icon)}
                    </span>
                    <div className="flex-1 min-w-0 w-full overflow-hidden">
                      <p className="font-bold text-sm sm:text-base text-gray-900 truncate w-full">{reply.title || 'Başlıksız'}</p>
                      {reply.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 truncate break-words w-full">{reply.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words w-full">Soru: {reply.question}</p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs text-gray-500">
                        <span>Sıra: {reply.sort_order}</span>
                        <span className="hidden sm:inline">İkon: {reply.icon}</span>
                        <span className="hidden sm:inline">Renk: {reply.color}</span>
                        {reply.is_active === 1 ? (
                          <span className="text-green-600 font-medium">● Aktif</span>
                        ) : (
                          <span className="text-gray-400">○ Pasif</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(reply)}
                      className="flex-1 sm:flex-initial text-xs sm:text-sm"
                    >
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 sm:flex-initial text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                      onClick={() => handleDelete(reply.id)}
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

